import { Suspense } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { MigratedLazyCodeEditor } from '@gonfalon/code-editor/MigratedLazyCodeEditor';
import { getSyntaxHighlightingOverrides } from '@gonfalon/code-editor/utils';
import { copyToClipboard } from '@gonfalon/dom/copyToClipboard';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toContext } from '@gonfalon/navigator/navigators';
import { Box } from '@launchpad-ui/box';
import { Button, Heading, LinkButton, Tooltip, TooltipTrigger, VisuallyHidden } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { formatContextForDisplay } from '../../../display/formatContextForDisplay';
import { useContextData } from '../../../hooks/useContextData';
import type { LDContextCommon } from '../../../types/types';

import styles from './ContextPopover.module.css';

interface ContextPopoverProps {
  /** Kind of the context being previewed (e.g. `user`). */
  readonly contextKind: string;
  /** Key of the context being previewed. */
  readonly contextKey: string;
  /** Project key used to resolve the context and build the external link. */
  readonly projectKey: string;
  /** Environment key used to resolve the context and build the external link. */
  readonly environmentKey: string;
  readonly isPreview?: boolean;
}

/**
 * Popover body for a context reference. Shows the context's JSON payload when the contexts search
 * knows the context, and an explicit not-seen panel when it does not — rather than reconstructing a
 * `{ kind, key }` payload out of the reference itself, which reads as recorded data.
 */
export function ContextPopover({
  contextKind,
  contextKey,
  projectKey,
  environmentKey,
  isPreview = false,
}: ContextPopoverProps) {
  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <div>
          <Message id="contexts.contextPopover.loadError" />
        </div>
      )}
    >
      <Suspense fallback={<ContextPopoverFallback />}>
        <ContextPopoverContent
          contextKind={contextKind}
          contextKey={contextKey}
          projectKey={projectKey}
          environmentKey={environmentKey}
          isPreview={isPreview}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function ContextPopoverFallback() {
  return (
    <Box display="flex" flexDirection="column" gap="$300" className={styles.container}>
      <div slot="header" className={styles.header}>
        <Heading slot="title">
          <Message id="contexts.contextPopover.fallbackHeading" />
        </Heading>
        <div>
          <Button size="small" variant="minimal" isDisabled>
            <VisuallyHidden>
              <Message id="contexts.contextPopover.fallbackCopyContext" />
            </VisuallyHidden>
            <Icon name="copy-clipboard" size="small" />
          </Button>
          <LinkButton variant="minimal" size="small" isDisabled>
            <VisuallyHidden>
              <Message id="contexts.contextPopover.fallbackViewInContexts" />
            </VisuallyHidden>
            <Icon name="link-external" size="small" />
          </LinkButton>
        </div>
      </div>
      <Box slot="body" display="flex" flexDirection="column" alignItems="flex-start" gap="$300" />
    </Box>
  );
}

const themeOverrides = {
  '.cm-content': {
    backgroundColor: 'var(--lp-color-bg-ui-secondary)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--lp-color-bg-ui-secondary)',
  },
  '.cm-activeLine': {
    backgroundColor: 'var(--lp-color-bg-ui-secondary)',
  },
};

const syntaxHighlightingOverrides = getSyntaxHighlightingOverrides([
  { tag: 'propertyName', color: 'var(--lp-color-brand-pink-base)' },
]);

const extensionConfig = {
  lineWrapping: true,
};

function ContextPopoverContent({
  contextKind,
  contextKey,
  projectKey,
  environmentKey,
  isPreview,
}: ContextPopoverProps) {
  const {
    data: context,
    isPending,
    isError,
    refetch,
  } = useContextData({
    projectKey,
    environmentKey,
    contextKind,
    contextKey,
    enabled: true,
  });

  if (isPending) {
    return <ContextPopoverFallback />;
  }

  // A failed lookup is not the same answer as a context the search has no record of, so it must not
  // fall through to the not-seen panel.
  if (isError) {
    return <ContextPopoverError onRetry={() => void refetch()} />;
  }

  if (context === undefined) {
    return <NotSeenContext contextKind={contextKind} contextKey={contextKey} isPreview={isPreview} />;
  }

  return (
    <ResolvedContext
      context={context}
      contextKind={contextKind}
      contextKey={contextKey}
      projectKey={projectKey}
      environmentKey={environmentKey}
      isPreview={isPreview}
    />
  );
}

function ContextPopoverError({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <Box display="flex" flexDirection="column" gap="$300" className={styles.container}>
      <div slot="header" className={styles.header}>
        <Heading slot="title">
          <Message id="contexts.contextPopover.errorHeading" />
        </Heading>
      </div>
      <Box slot="body" className={styles.messageBody}>
        <p className={styles.explanation}>
          <Message id="contexts.contextPopover.errorBody" />
        </p>
        <div>
          <Button size="small" onPress={onRetry}>
            {msg('contexts.contextPopover.retry')}
          </Button>
        </div>
      </Box>
    </Box>
  );
}

function ResolvedContext({
  context,
  contextKind,
  contextKey,
  projectKey,
  environmentKey,
  isPreview,
}: ContextPopoverProps & { readonly context: LDContextCommon }) {
  const formattedContext = formatContextForDisplay(context, contextKind);
  const contextString = JSON.stringify(formattedContext, null, 2);

  return (
    <Box display="flex" flexDirection="column" gap="$300" className={styles.container}>
      <div slot="header" className={styles.header}>
        <Heading slot="title">
          <Message id="contexts.contextPopover.heading" />
        </Heading>
        <div>
          <ActionTooltip isPreview={isPreview} label={<Message id="contexts.contextPopover.copyContext" />}>
            <Button
              size="small"
              variant="minimal"
              onPress={() => {
                void copyToClipboard(contextString, 'Copied!');
              }}
            >
              <VisuallyHidden>
                <Message id="contexts.contextPopover.copyContext" />
              </VisuallyHidden>
              <Icon name="copy-clipboard" size="small" />
            </Button>
          </ActionTooltip>
          <ActionTooltip isPreview={isPreview} label={<Message id="contexts.contextPopover.viewFullDetails" />}>
            <LinkButton
              variant="minimal"
              size="small"
              href={toContext({ projectKey, kind: contextKind, key: contextKey, environmentKey })}
              target="_blank"
            >
              <VisuallyHidden>
                <Message id="contexts.contextPopover.viewInContexts" />
              </VisuallyHidden>
              <Icon name="link-external" size="small" />
            </LinkButton>
          </ActionTooltip>
        </div>
      </div>
      <Box slot="body" display="flex" flexDirection="column" alignItems="flex-start" gap="$300">
        <MigratedLazyCodeEditor
          id="context-viewer"
          mode="json"
          readOnly
          autoFocus={!isPreview}
          label={null}
          value={contextString}
          themeOverrides={themeOverrides}
          syntaxHighlightingOverrides={syntaxHighlightingOverrides}
          className={styles.textArea}
          screenReaderLabel="Context JSON viewer"
          extensionConfigs={extensionConfig}
          fullscreen={false}
        />
      </Box>
    </Box>
  );
}

/**
 * Panel for a context the contexts search returned no record for. It shows the kind and key the
 * reference carries — labelled as such, not dressed up as a recorded payload — and says why there
 * is nothing else to show. There is no view-in-Contexts action because that page has no record
 * either.
 *
 * The contexts search only covers the last 30 days, so a miss means "not seen in that window". It
 * cannot distinguish a context that was never evaluated from one that has aged out, and the copy
 * does not claim otherwise.
 */
function NotSeenContext({
  contextKind,
  contextKey,
  isPreview,
}: {
  readonly contextKind: string;
  readonly contextKey: string;
  readonly isPreview: boolean | undefined;
}) {
  return (
    <Box display="flex" flexDirection="column" gap="$300" className={styles.container}>
      <div slot="header" className={styles.header}>
        <Heading slot="title">
          <Message id="contexts.contextPopover.notSeenHeading" />
        </Heading>
        <div>
          <ActionTooltip isPreview={isPreview} label={<Message id="contexts.contextPopover.notSeenCopyKey" />}>
            <Button
              size="small"
              variant="minimal"
              onPress={() => {
                void copyToClipboard(contextKey, 'Copied!');
              }}
            >
              <VisuallyHidden>
                <Message id="contexts.contextPopover.notSeenCopyKey" />
              </VisuallyHidden>
              <Icon name="copy-clipboard" size="small" />
            </Button>
          </ActionTooltip>
        </div>
      </div>
      <Box slot="body" className={styles.messageBody}>
        <dl className={styles.identity}>
          <dt className={styles.identityTerm}>
            <Message id="contexts.contextPopover.notSeenKindLabel" />
          </dt>
          <dd className={styles.identityValue}>{contextKind}</dd>
          <dt className={styles.identityTerm}>
            <Message id="contexts.contextPopover.notSeenKeyLabel" />
          </dt>
          <dd className={styles.identityValue}>{contextKey}</dd>
        </dl>
        <p className={styles.explanation}>
          <Message id="contexts.contextPopover.notSeenBody" />
        </p>
      </Box>
    </Box>
  );
}

function ActionTooltip({
  isPreview,
  label,
  children,
}: {
  readonly isPreview: boolean | undefined;
  readonly label: ReactNode;
  readonly children: ReactElement;
}) {
  if (isPreview) {
    return children;
  }

  return (
    <TooltipTrigger>
      {children}
      <Tooltip placement="bottom">{label}</Tooltip>
    </TooltipTrigger>
  );
}
